import { useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Printer, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import ProUpsell from "@/components/ProUpsell";

const CSV_EXPORTS = [
  { key: "apiary", label: "🍯 Apiary summary (location + zone + 12-mo metrics)", highlight: true },
  { key: "journals", label: "Inspection journals" },
  { key: "harvests", label: "Harvests" },
  { key: "hives",    label: "Hives" },
  { key: "treatments", label: "Treatments & feedings" },
];

export default function ExportsTab() {
  const [busy, setBusy] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [upsell, setUpsell] = useState(false);

  const downloadCsv = async (key) => {
    setBusy(key);
    try {
      const r = await api.get(`/exports/${key}.csv`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${key}.csv`);
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  const downloadApiaryPdf = async () => {
    setBusy("apiary-pdf");
    try {
      const r = await api.get("/exports/apiary.pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `apiary-summary-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Apiary summary PDF downloaded");
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  const fetchSummary = async () => {
    setBusy("summary");
    try {
      const r = await api.get("/exports/annual-summary", { params: { year } });
      setSummary(r.data);
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const printSummary = () => {
    if (!summary) return;
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="exports-tab">
      <section className="bg-white border border-stone-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-4 h-4 text-amber-700" />
          <h3 className="font-display text-lg font-medium">CSV exports</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 ml-2">Pro</span>
        </div>
        <p className="text-sm text-stone-600 mb-4">Download your full records as CSV — perfect for spreadsheets and back-ups.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {CSV_EXPORTS.map((c) => (
            <div key={c.key} className={`border rounded-xl p-3 flex items-center justify-between gap-2 ${c.highlight ? "border-amber-200 bg-amber-50/40 sm:col-span-2" : "border-stone-200"}`}>
              <div className="text-sm font-medium text-stone-800">{c.label}</div>
              <div className="flex gap-2 shrink-0">
                {c.key === "apiary" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={downloadApiaryPdf}
                    disabled={busy === "apiary-pdf"}
                    data-testid="export-apiary-pdf-btn"
                    title="One-page printable cover sheet"
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    {busy === "apiary-pdf" ? "…" : "PDF"}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={c.highlight ? "default" : "outline"}
                  className={c.highlight ? "bg-yellow-400 text-amber-900 hover:bg-yellow-300" : ""}
                  onClick={() => downloadCsv(c.key)}
                  disabled={busy === c.key}
                  data-testid={`export-${c.key}-btn`}
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {busy === c.key ? "Exporting…" : "CSV"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-stone-200 rounded-2xl p-6 print:border-0 print:rounded-none" data-testid="annual-summary-section">
        <div className="flex items-center gap-2 mb-1 print:hidden">
          <FileText className="w-4 h-4 text-amber-700" />
          <h3 className="font-display text-lg font-medium">Annual report</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 ml-2">Pro</span>
        </div>
        <p className="text-sm text-stone-600 mb-4 print:hidden">A printable summary of one year of beekeeping. Use your browser's print dialog to save as PDF.</p>
        <div className="flex items-end gap-2 flex-wrap mb-5 print:hidden">
          <div>
            <Label>Year</Label>
            <Input
              type="number"
              min={1900}
              max={2100}
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || new Date().getFullYear())}
              className="w-32"
              data-testid="annual-year-input"
            />
          </div>
          <Button onClick={fetchSummary} disabled={busy === "summary"} data-testid="generate-annual-btn">
            <Sparkles className="w-4 h-4 mr-1.5" />
            {busy === "summary" ? "Generating…" : "Generate"}
          </Button>
          {summary && (
            <Button onClick={printSummary} variant="outline" data-testid="print-annual-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
            </Button>
          )}
        </div>

        {summary && (
          <div className="space-y-4" data-testid="annual-summary-content">
            <header className="text-center mb-4">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 print:text-stone-700">Annual report</div>
              <h2 className="font-display text-3xl font-bold text-stone-900 mt-1">{summary.year}</h2>
              <p className="text-xs text-stone-500 mt-1">Generated {new Date(summary.generated_at).toLocaleDateString()}</p>
            </header>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              <Stat label="Hives" value={summary.totals.hives} />
              <Stat label="Inspections" value={summary.totals.inspections} />
              <Stat label="Harvests" value={summary.totals.harvests} />
              <Stat label="Total lb" value={summary.totals.total_harvest_lb} />
              <Stat label="Treatments" value={summary.totals.treatments} />
              <Stat label="Feedings" value={summary.totals.feedings} />
            </div>
            <table className="w-full text-sm border-collapse mt-6" data-testid="annual-hive-table">
              <thead>
                <tr className="border-b-2 border-stone-300 text-xs uppercase tracking-[0.12em] text-stone-600">
                  <th className="py-2 text-left font-semibold">Hive</th>
                  <th className="py-2 text-left font-semibold">Yard</th>
                  <th className="py-2 text-left font-semibold">Strength</th>
                  <th className="py-2 text-right font-semibold">Inspections</th>
                  <th className="py-2 text-right font-semibold">Harvest (lb)</th>
                  <th className="py-2 text-right font-semibold">Treatments</th>
                </tr>
              </thead>
              <tbody>
                {summary.hives.map((h) => (
                  <tr key={h.id} className="border-b border-stone-200">
                    <td className="py-2 text-stone-900">{h.name}</td>
                    <td className="py-2 text-stone-600">{h.yard || "—"}</td>
                    <td className="py-2 text-stone-600 capitalize">{h.strength || "—"}</td>
                    <td className="py-2 text-right tabular-nums">{h.inspections}</td>
                    <td className="py-2 text-right tabular-nums font-medium">{h.harvest_lb}</td>
                    <td className="py-2 text-right tabular-nums">{h.treatments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ProUpsell open={upsell} onOpenChange={setUpsell} feature="exports" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-stone-200 rounded-xl p-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold text-stone-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}
